import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useLang } from "../../context/LanguageContext";
import { mvpApi } from "../../../services/api";
import { isMobileScannerClient } from "../../utils/isMobileScannerClient";
import DocumentCameraView, { type DocumentCameraCaptureMeta } from "./DocumentCameraView";
import DocumentCornerEditor from "./DocumentCornerEditor";
import PassportScanExamplesModal from "../ui/PassportScanExamplesModal";
import "./MobileDocumentScanner.css";

type ScanPage = {
  id: string;
  file: File;
  previewUrl: string;
  /** True when the page was captured via the in-app camera and pre-flattened on device. */
  flattened?: boolean;
};

function genId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatFileShortName(f: File): string {
  const n = f.name || "";
  return n.length > 28 ? `${n.slice(0, 26)}…` : n;
}

/** Horizontal translate for each thumbnail while previewing reorder (slot ≈ thumb width + flex gap). */
function thumbnailReorderPreviewOffsetPx(idx: number, from: number, hover: number, slotPx: number): number {
  if (from === hover || slotPx <= 0) return 0;
  if (from < hover) {
    if (idx === from) return (hover - from) * slotPx;
    if (idx > from && idx <= hover) return -slotPx;
    return 0;
  }
  if (idx === from) return -(from - hover) * slotPx;
  if (idx >= hover && idx < from) return slotPx;
  return 0;
}

/**
 * Nearest page slot under the pointer using `[data-gm-thumb-slot]` layout rects.
 */
function resolveThumbnailStripHoverIndex(
  strip: HTMLElement,
  pageCount: number,
  clientX: number,
  clientY: number,
): number | null {
  if (pageCount <= 0) return null;

  const rect = strip.getBoundingClientRect();
  const verticalMargin = 36;
  if (clientY < rect.top - verticalMargin || clientY > rect.bottom + verticalMargin) {
    return null;
  }

  const slots = strip.querySelectorAll("[data-gm-thumb-slot]");
  if (slots.length === pageCount) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < slots.length; i++) {
      const r = slots[i].getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const dist = Math.abs(clientX - cx);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  const slotSample =
    strip.querySelector("[data-gm-thumb-slot]") ?? strip.querySelector("[data-gm-thumb-idx]");
  if (!(slotSample instanceof HTMLElement)) return null;
  const slotW = slotSample.getBoundingClientRect().width;
  const cs = getComputedStyle(strip);
  const gapStr = cs.gap || cs.columnGap || "0px";
  const gap = parseFloat(gapStr) || 0;
  const slotPx = slotW + gap;

  const x = clientX - rect.left + strip.scrollLeft;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < pageCount; i++) {
    const center = i * slotPx + slotW / 2;
    const d = Math.abs(x - center);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Persisted so passport scan examples are auto-shown at most once per browser unless reopened via link / “?”. */
const PASSPORT_EXAMPLES_SEEN_KEY = "gm_passport_scan_examples_seen_v1";

/** Fewer scanned pages triggers a passport completeness warning before building the PDF (passport variant). */
const PASSPORT_EXPECTED_PAGES_WARN_THRESHOLD = 14;

function passportExamplesPreviouslyDismissed(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(PASSPORT_EXAMPLES_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markPassportExamplesDismissed(): void {
  try {
    localStorage.setItem(PASSPORT_EXAMPLES_SEEN_KEY, "1");
  } catch {
    /* private mode / storage quota */
  }
}

function isScanMergeTransportFailure(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const e = err as {
    code?: string;
    message?: string;
    response?: { status?: number } | undefined;
    request?: unknown;
  };
  const code = String(e.code ?? "");
  if (code === "ECONNABORTED" || code === "ETIMEDOUT" || code === "ERR_NETWORK") return true;
  const msg = String(e.message ?? "").toLowerCase();
  if (msg.includes("network error") || msg.includes("networkerror")) return true;
  if (msg.includes("timeout") || msg.includes("timed out") || msg.includes("aborted")) return true;
  const st = e.response?.status;
  if (e.request != null && (st === undefined || st === 0)) return true;
  return false;
}

function logScanMergeTransportFailure(err: unknown, context: Record<string, unknown>): void {
  const e = err as { name?: unknown; code?: unknown; message?: unknown; response?: { status?: unknown } };
  console.warn("[document-scan-merge]", {
    ...context,
    errName: e?.name,
    errCode: e?.code,
    errMessage: typeof e?.message === "string" ? e.message : undefined,
    httpStatus:
      e?.response && typeof e.response === "object" && "status" in e.response
        ? (e.response as { status?: number }).status
        : undefined,
    navigatorOnLine:
      typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : undefined,
  });
}

/** Client-side assemble / handoff failures (memory pressure, quota, bad device state). Not network. */
function isLikelyDevicePdfFailure(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const name = String((err as { name?: string }).name ?? "");
  if (name === "RangeError" || name === "QuotaExceededError") return true;
  if (
    typeof DOMException !== "undefined" &&
    err instanceof DOMException &&
    (err.name === "QuotaExceededError" || err.code === 22 || err.code === DOMException.QUOTA_EXCEEDED_ERR)
  ) {
    return true;
  }
  const msg = String((err as { message?: string }).message ?? "").toLowerCase();
  if (msg.includes("quota") || msg.includes("allocation") || msg.includes("memory") || msg.includes("heap")) return true;
  return false;
}

function logScanDevicePdfFailure(err: unknown, context: Record<string, unknown>): void {
  const e = err as { name?: unknown; message?: unknown };
  console.warn("[document-scan-pdf-device]", {
    ...context,
    errName: e?.name,
    errMessage: typeof e?.message === "string" ? e.message : undefined,
    navigatorOnLine:
      typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : undefined,
  });
}

export interface MobileDocumentScannerProps {
  open: boolean;
  titleKey?: string;
  onClose: () => void;
  /** Receives merged PDF (`application/pdf`) from the backend. */
  onComplete: (
    pdfFile: File,
    meta?: { ocrApplied: boolean; ocrReason?: string; sourcePageCount?: number },
  ) => void | Promise<void>;
  /** Request a searchable OCR layer on the server (Tesseract). Defaults to false (same as full-passport scan). */
  ocrEnabled?: boolean;
  /** Optional Tesseract language list, e.g. ``"eng+spa"``. Defaults to backend default. */
  ocrLanguages?: string;
  /**
   * ``passport``: instructive camera; after each capture opens corner editor, then review hub - same PDF pipeline everywhere.
   * ``standard``: legacy gallery-first sheet (avoid unless you need the old UX).
   */
  variant?: "standard" | "passport";
  /**
   * Passport-only marketing: scan examples modal, first-launch auto-open, ``?`` on review, passport intro copy on the sheet.
   * Full-passport identity flow should set this when ``docType === "passport"``.
   */
  passportExamplesSuite?: boolean;
  /**
   * When true with enough pages missing vs. ``PASSPORT_EXPECTED_PAGES_WARN_THRESHOLD``, warn before Create PDF.
   * Only the full-passport identity flow should enable this.
   */
  passportLowPageCountWarn?: boolean;
  /**
   * After the PDF API returns, ``onComplete`` may await upload + AI. While that runs, show this label
   * on the busy button instead of {@link documentScanner.processing} (e.g. identity upload flow).
   */
  afterPdfBusyLabelKey?: string;
  /**
   * When true: close the scanner as soon as the merged PDF exists (before awaiting {@link onComplete}).
   * Use when the parent shows upload/AI progress (e.g. identity passport onboarding card).
   */
  releaseUiBeforeOnComplete?: boolean;
  /**
   * ``light`` (default): applicant intake sheet. ``dark``: participant home / production dark chrome.
   */
  appearance?: "light" | "dark";
}

/**
 * Bottom-sheet style scanner: capture multiple pages, reorder, flatten server-side → one PDF.
 * On non-mobile UA, shows messaging only - capture runs on phones/tablets per product spec.
 */
export default function MobileDocumentScanner({
  open,
  titleKey = "documentScanner.title",
  onClose,
  onComplete,
  ocrEnabled = false,
  ocrLanguages,
  variant = "passport",
  passportExamplesSuite = false,
  passportLowPageCountWarn = false,
  afterPdfBusyLabelKey,
  releaseUiBeforeOnComplete = false,
  appearance = "light",
}: MobileDocumentScannerProps) {
  const { t } = useLang();
  const [pages, setPages] = useState<ScanPage[]>([]);
  const [busy, setBusy] = useState(false);
  /** True after merged PDF bytes return and until ``onComplete`` settles (handoff to parent). */
  const [busyAwaitingHandoff, setBusyAwaitingHandoff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [previewPageId, setPreviewPageId] = useState<string | null>(null);
  const [cornerEditorOpen, setCornerEditorOpen] = useState(false);
  useEffect(() => {
    cornerEditorOpenRef.current = cornerEditorOpen;
  }, [cornerEditorOpen]);
  /** Passport: user closed review sheet overlay - show minimal scan sheet until they resume review or open camera. */
  const [passportReviewSkipped, setPassportReviewSkipped] = useState(false);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cornerEditorOpenRef = useRef(false);
  /** When set, next capture inserts after this page index (review «Insert page»). */
  const pendingInsertAfterIdxRef = useRef<number | null>(null);
  const reviewDeleteConfirmOpenRef = useRef(false);
  const pagesRef = useRef<ScanPage[]>([]);
  const [reviewDeleteConfirmOpen, setReviewDeleteConfirmOpen] = useState(false);
  /** Passport: straighten corners immediately after each shutter (before review sheet). */
  const [passportPostCaptureEditId, setPassportPostCaptureEditId] = useState<string | null>(null);
  const [thumbnailDragFromIdx, setThumbnailDragFromIdx] = useState<number | null>(null);
  /** Drop target index while dragging thumbnails (preview shift animation). */
  const [thumbnailHoverDropIdx, setThumbnailHoverDropIdx] = useState<number | null>(null);
  /** During reorder, strip uses touch-none so horizontal scroll does not fight the drag. */
  const [thumbStripReorderLock, setThumbStripReorderLock] = useState(false);
  const [thumbSlotPx, setThumbSlotPx] = useState(96);
  const thumbStripRef = useRef<HTMLDivElement>(null);
  /** Suppress the synthetic click after a successful pointer reorder (indices/pages shift under the finger). */
  const thumbSkipNextClickRef = useRef(false);
  /** Passport: Retake from review opens camera to replace the page at this index. */
  const passportReplacePageIdxRef = useRef<number | null>(null);
  const [passportExamplesOpen, setPassportExamplesOpen] = useState(false);
  /** Review overlay: confirm before leaving and losing all captured pages (Close / Escape). */
  const [reviewLeaveConfirmOpen, setReviewLeaveConfirmOpen] = useState(false);
  const reviewLeaveConfirmOpenRef = useRef(false);
  /** Passport: warn when scanning fewer than usual passport pages before Create PDF. */
  const [passportLowPageWarnOpen, setPassportLowPageWarnOpen] = useState(false);
  const passportLowPageWarnOpenRef = useRef(false);
  /** Passport review: full-screen read-only view of the current page. */
  const [passportReviewFullViewOpen, setPassportReviewFullViewOpen] = useState(false);
  const passportReviewFullViewOpenRef = useRef(false);

  const syncPassportReviewFullView = useCallback((next: boolean) => {
    passportReviewFullViewOpenRef.current = next;
    setPassportReviewFullViewOpen(next);
  }, []);

  /** Merge step failed (client ~10s timeout / offline / bad signal): user can retry or cancel and close the scanner. */
  const [scanConnectionModalOpen, setScanConnectionModalOpen] = useState(false);
  const scanConnectionModalOpenRef = useRef(false);

  const syncScanConnectionModal = useCallback((next: boolean) => {
    scanConnectionModalOpenRef.current = next;
    setScanConnectionModalOpen(next);
  }, []);

  /** Rare: File / Blob handoff or parent ``onComplete`` failed on device (e.g. memory pressure). */
  const [scanDevicePdfModalOpen, setScanDevicePdfModalOpen] = useState(false);
  const scanDevicePdfModalOpenRef = useRef(false);

  const syncScanDevicePdfModal = useCallback((next: boolean) => {
    scanDevicePdfModalOpenRef.current = next;
    setScanDevicePdfModalOpen(next);
  }, []);

  const dismissPassportExamples = useCallback(() => {
    markPassportExamplesDismissed();
    setPassportExamplesOpen(false);
  }, []);

  useEffect(() => {
    reviewDeleteConfirmOpenRef.current = reviewDeleteConfirmOpen;
  }, [reviewDeleteConfirmOpen]);

  useEffect(() => {
    reviewLeaveConfirmOpenRef.current = reviewLeaveConfirmOpen;
  }, [reviewLeaveConfirmOpen]);

  useEffect(() => {
    passportLowPageWarnOpenRef.current = passportLowPageWarnOpen;
  }, [passportLowPageWarnOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setPassportLowPageWarnOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, passportLowPageWarnOpen]);

  pagesRef.current = pages;
  const mobile = isMobileScannerClient();
  const cameraOpenRef = useRef(cameraOpen);
  cameraOpenRef.current = cameraOpen;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeCameraFromUiRef = useRef<() => void>(() => {});

  /** Passport: closing the camera should land on the review screen when pages exist (not the hub sheet). */
  const closeCameraFromUi = useCallback(() => {
    const insertAfter = pendingInsertAfterIdxRef.current;
    pendingInsertAfterIdxRef.current = null;
    setCameraOpen(false);
    if (variant !== "passport") return;
    setPassportReviewSkipped(false);
    const list = pagesRef.current;
    if (!list.length) return;
    const targetId =
      insertAfter !== null && insertAfter >= 0 && insertAfter < list.length
        ? list[insertAfter].id
        : list[list.length - 1].id;
    setPreviewPageId(targetId);
  }, [variant]);
  closeCameraFromUiRef.current = closeCameraFromUi;

  const title = t(titleKey as Parameters<typeof t>[0]);

  /**
   * Single history integration for sheet + in-app camera (no nested hooks).
   * After opening the scanner we push a sentinel; opening the camera pushes
   * another. Back/gesture pops the top sentinel first (closes camera), then
   * the sheet sentinel (closes scanner).
   */
  useEffect(() => {
    if (!open) return;

    const onPop = () => {
      if (busyRef.current) return;
      if (scanDevicePdfModalOpenRef.current) {
        syncScanDevicePdfModal(false);
        return;
      }
      if (scanConnectionModalOpenRef.current) {
        syncScanConnectionModal(false);
        return;
      }
      const st = window.history.state as { __gmScanner?: boolean; layer?: string } | null;
      if (st?.__gmScanner && st.layer === "sheet") {
        if (cameraOpenRef.current) closeCameraFromUiRef.current();
        return;
      }
      if (!st?.__gmScanner) {
        onCloseRef.current();
      }
    };

    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
    };
  }, [open, syncScanConnectionModal, syncScanDevicePdfModal]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = () => {
      if (cancelled || typeof window === "undefined") return;
      try {
        window.history.pushState({ __gmScanner: true, layer: "sheet" }, "", window.location.href);
      } catch {
        /* embedded WebViews sometimes block pushState */
      }
    };
    // Defer until after paint so the portal/backdrop is committed before history mutations (some mobile WebViews flash blank otherwise).
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !cameraOpen || !mobile) return;
    let cancelled = false;
    const run = () => {
      if (cancelled || typeof window === "undefined") return;
      try {
        window.history.pushState({ __gmScanner: true, layer: "camera" }, "", window.location.href);
      } catch {
        /* ignore */
      }
    };
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [open, cameraOpen, mobile]);

  const revokeUrls = useCallback((plist: ScanPage[]) => {
    plist.forEach((p) => {
      try {
        URL.revokeObjectURL(p.previewUrl);
      } catch {
        /* ignore */
      }
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setPages((prev) => {
        revokeUrls(prev);
        return [];
      });
      setError(null);
      setBusy(false);
      setCameraOpen(false);
      setPreviewPageId(null);
      setPassportPostCaptureEditId(null);
      setPassportReviewSkipped(false);
      setPassportExamplesOpen(false);
      setReviewLeaveConfirmOpen(false);
      setPassportLowPageWarnOpen(false);
      syncScanConnectionModal(false);
      syncScanDevicePdfModal(false);
      syncPassportReviewFullView(false);
      setThumbStripReorderLock(false);
    }
  }, [open, revokeUrls, syncScanConnectionModal, syncScanDevicePdfModal, syncPassportReviewFullView]);

  useEffect(() => {
    if (!open || !(scanConnectionModalOpen || scanDevicePdfModalOpen)) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (scanDevicePdfModalOpenRef.current) {
        syncScanDevicePdfModal(false);
      } else {
        syncScanConnectionModal(false);
      }
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    open,
    scanConnectionModalOpen,
    scanDevicePdfModalOpen,
    syncScanConnectionModal,
    syncScanDevicePdfModal,
  ]);

  /** Passport marketing: once per browser, surface scan examples before the first capture (never auto-open again after dismiss). */
  useEffect(() => {
    if (!open || variant !== "passport" || !passportExamplesSuite || !mobile || cameraOpen || pages.length > 0) return;
    if (passportExamplesPreviouslyDismissed()) return;
    setPassportExamplesOpen(true);
  }, [open, variant, passportExamplesSuite, mobile, cameraOpen, pages.length]);

  /** Passport: review is the hub - open it whenever there are pages and nothing else is covering the flow. */
  useEffect(() => {
    if (!open || variant !== "passport" || !mobile) return;
    if (pages.length === 0) return;
    if (cameraOpen) return;
    if (passportPostCaptureEditId != null) return;
    if (previewPageId != null) return;
    if (passportReviewSkipped) return;
    const last = pages[pages.length - 1];
    if (last) setPreviewPageId(last.id);
  }, [
    open,
    variant,
    mobile,
    pages,
    pages.length,
    cameraOpen,
    passportPostCaptureEditId,
    previewPageId,
    passportReviewSkipped,
  ]);

  useEffect(() => {
    if (variant !== "passport") return;
    if (pages.length === 0) setPassportReviewSkipped(false);
  }, [variant, pages.length]);

  useEffect(() => {
    if (!previewPageId) {
      setCornerEditorOpen(false);
      setReviewDeleteConfirmOpen(false);
      setReviewLeaveConfirmOpen(false);
      setPassportLowPageWarnOpen(false);
      syncPassportReviewFullView(false);
    }
  }, [previewPageId, syncPassportReviewFullView]);

  useEffect(() => {
    if (!previewPageId || !open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (variant === "passport" && passportReviewFullViewOpenRef.current) {
        syncPassportReviewFullView(false);
        return;
      }
      if (cornerEditorOpenRef.current) {
        setCornerEditorOpen(false);
        return;
      }
      if (passportLowPageWarnOpenRef.current) {
        setPassportLowPageWarnOpen(false);
        return;
      }
      if (reviewLeaveConfirmOpenRef.current) {
        setReviewLeaveConfirmOpen(false);
        return;
      }
      if (reviewDeleteConfirmOpenRef.current) {
        setReviewDeleteConfirmOpen(false);
        return;
      }
      if (pagesRef.current.length > 0) {
        setReviewLeaveConfirmOpen(true);
        return;
      }
      if (variant === "passport") setPassportReviewSkipped(true);
      setPreviewPageId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewPageId, open, variant, syncPassportReviewFullView]);

  const appendFiles = useCallback((list: FileList | File[]) => {
    const incoming = Array.from(list).filter((f) => String(f?.type || "").startsWith("image/"));
    if (!incoming.length) return;
    setPages((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        next.push({
          id: genId(),
          file,
          previewUrl: URL.createObjectURL(file),
        });
      }
      return next;
    });
    setError(null);
  }, []);

  const appendCapturedPage = useCallback(
    (file: File, meta: DocumentCameraCaptureMeta) => {
      setPages((prev) => [
        ...prev,
        {
          id: genId(),
          file,
          previewUrl: URL.createObjectURL(file),
          flattened: meta.flattened,
        },
      ]);
      setError(null);
    },
    [],
  );

  const removePage = useCallback(
    (id: string) => {
      const snapshot = pagesRef.current;
      const idx = snapshot.findIndex((p) => p.id === id);
      if (idx < 0) return;

      let nextPreview: string | null | undefined;
      if (previewPageId === id) {
        const next = snapshot.filter((p) => p.id !== id);
        nextPreview = next.length === 0 ? null : next[Math.min(idx, next.length - 1)].id;
      }

      setPages((prev) => {
        const i = prev.findIndex((p) => p.id === id);
        if (i < 0) return prev;
        const page = prev[i];
        try {
          URL.revokeObjectURL(page.previewUrl);
        } catch {
          /* ignore */
        }
        return prev.filter((p) => p.id !== id);
      });

      if (nextPreview !== undefined) {
        setPreviewPageId(nextPreview);
      }
    },
    [previewPageId],
  );

  const reorderThumbnailPages = useCallback((fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    setPages((prev) => {
      if (fromIdx < 0 || toIdx < 0 || fromIdx >= prev.length || toIdx >= prev.length) return prev;
      const next = [...prev];
      const [row] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, row);
      return next;
    });
  }, []);

  /**
   * Touch: long-press thumbnail body to reorder (row scrolls freely until then).
   * Touch on the dedicated drag handle (or mouse on the thumbnail): immediate drag.
   * Hover updates are rAF-batched to reduce flicker.
   */
  const beginThumbnailReorderGesture = useCallback(
    (fromIdx: number, options?: { immediate?: boolean }) =>
    (e: ReactPointerEvent<HTMLElement>) => {
      if (busy) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;

      e.stopPropagation();

      const target = e.currentTarget;
      const pointerId = e.pointerId;
      const startX = e.clientX;
      const startY = e.clientY;
      let lastX = startX;
      let lastY = startY;

      const isFinePointer = e.pointerType === "mouse";
      const isImmediate = Boolean(options?.immediate);
      const LONG_PRESS_MS = 280;
      /** Cancel long-press only on obvious horizontal swipe-scroll (before reorder activates). */
      const SCROLL_PREEMPT_MIN_HORIZ = 26;
      const SCROLL_PREEMPT_DOMINANCE = 1.22;

      let reorderActive = isFinePointer || isImmediate;
      let longPressTimer: number | null = null;
      let finished = false;
      let lastResolvedHover = fromIdx;
      const DRAG_SLOP_PX = 6;

      let rafScheduled = false;
      let pendingHoverIdx: number | null = null;

      const flushHoverRaf = () => {
        rafScheduled = false;
        if (pendingHoverIdx === null) return;
        const v = pendingHoverIdx;
        setThumbnailHoverDropIdx((prev) => (prev === v ? prev : v));
      };

      const scheduleHover = (idx: number) => {
        pendingHoverIdx = idx;
        if (!rafScheduled) {
          rafScheduled = true;
          requestAnimationFrame(flushHoverRaf);
        }
      };

      const unlockStrip = () => setThumbStripReorderLock(false);

      const clearDragUi = () => {
        setThumbnailDragFromIdx(null);
        setThumbnailHoverDropIdx(null);
        unlockStrip();
      };

      const activateReorder = () => {
        if (finished || reorderActive) return;
        reorderActive = true;
        setThumbStripReorderLock(true);
        try {
          target.setPointerCapture(pointerId);
        } catch {
          /* WebViews */
        }
        setThumbnailDragFromIdx(fromIdx);
        setThumbnailHoverDropIdx(fromIdx);
        lastResolvedHover = fromIdx;
        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
          try {
            navigator.vibrate(10);
          } catch {
            /* ignore */
          }
        }
      };

      const clearLongPressTimer = () => {
        if (longPressTimer !== null) {
          window.clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      };

      if (isImmediate || isFinePointer) {
        activateReorder();
      } else {
        longPressTimer = window.setTimeout(() => {
          longPressTimer = null;
          if (finished) return;
          activateReorder();
        }, LONG_PRESS_MS);
      }

      const detachListeners = () => {
        document.removeEventListener("pointermove", onMove, true);
        document.removeEventListener("pointerup", finish, true);
        document.removeEventListener("pointercancel", finish, true);
      };

      const finish = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        if (finished) return;
        finished = true;
        clearLongPressTimer();
        detachListeners();

        try {
          target.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }

        if (!reorderActive) {
          clearDragUi();
          return;
        }

        const x = Number.isFinite(ev.clientX) ? ev.clientX : lastX;
        const y = Number.isFinite(ev.clientY) ? ev.clientY : lastY;
        const stripEl = thumbStripRef.current;
        const geomDrop =
          stripEl != null ? resolveThumbnailStripHoverIndex(stripEl, pagesRef.current.length, x, y) : null;

        const hitEl = document.elementFromPoint(x, y);
        const hit = hitEl?.closest("[data-gm-thumb-idx]");
        const raw = hit?.getAttribute("data-gm-thumb-idx");

        let toIdx = fromIdx;
        if (geomDrop !== null) {
          toIdx = geomDrop;
        } else if (raw != null) {
          const parsed = parseInt(raw, 10);
          if (!Number.isNaN(parsed)) toIdx = parsed;
        } else {
          toIdx = lastResolvedHover;
        }

        const dist = Math.hypot(x - startX, y - startY);
        const meaningfulDrag = dist >= DRAG_SLOP_PX || fromIdx !== toIdx;

        if (!meaningfulDrag || fromIdx === toIdx) {
          clearDragUi();
          return;
        }

        reorderThumbnailPages(fromIdx, toIdx);
        thumbSkipNextClickRef.current = true;
        clearDragUi();
      };

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        lastX = ev.clientX;
        lastY = ev.clientY;

        if (!reorderActive) {
          if (!isFinePointer && longPressTimer !== null) {
            const dx = Math.abs(ev.clientX - startX);
            const dy = Math.abs(ev.clientY - startY);
            if (dx > SCROLL_PREEMPT_MIN_HORIZ && dx > dy * SCROLL_PREEMPT_DOMINANCE) {
              clearLongPressTimer();
            }
          }
          return;
        }

        ev.preventDefault();

        const strip = thumbStripRef.current;
        if (!strip) return;
        const parsed = resolveThumbnailStripHoverIndex(strip, pagesRef.current.length, ev.clientX, ev.clientY);
        if (parsed === null) return;

        lastResolvedHover = parsed;
        scheduleHover(parsed);
      };

      document.addEventListener("pointermove", onMove, { capture: true, passive: false });
      document.addEventListener("pointerup", finish, { capture: true });
      document.addEventListener("pointercancel", finish, { capture: true });
    },
    [busy, reorderThumbnailPages],
  );

  const move = (idx: number, delta: number) => {
    setPages((prev) => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const parseBlobError = async (blob: Blob): Promise<string | null> => {
    try {
      const ct = blob.type || "";
      if (!ct.includes("json") && !ct.includes("text")) return null;
      const text = await blob.text();
      const j = JSON.parse(text);
      const d = j?.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg);
      return typeof j?.message === "string" ? j.message : null;
    } catch {
      return null;
    }
  };

  const buildPdf = async () => {
    if (!mobile) return;
    if (!pages.length) {
      setError(t("documentScanner.needOnePage"));
      return;
    }
    setBusyAwaitingHandoff(false);
    setBusy(true);
    setError(null);
    try {
      const files = pages.map((p) => p.file);
      const skipAutoCropFlags = pages.map((p) => Boolean(p.flattened));
      const res = await mvpApi.processDocumentScan(files, ocrEnabled, ocrLanguages, skipAutoCropFlags);
      const blob = res.data as Blob;
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("empty_pdf");
      }
      if ((blob.type && blob.type.includes("json")) || blob.type.includes("text")) {
        const msg = (await parseBlobError(blob)) || t("documentScanner.errorGeneric");
        throw new Error(msg);
      }
      syncScanConnectionModal(false);
      syncScanDevicePdfModal(false);
      const headers = res?.headers || {};
      const ocrStatus = String(headers["x-scan-ocr-status"] || headers["X-Scan-OCR-Status"] || "").toLowerCase();
      const ocrReason = String(headers["x-scan-ocr-reason"] || headers["X-Scan-OCR-Reason"] || "") || undefined;
      let pdfFile: File;
      try {
        pdfFile = new File([blob], "scan.pdf", { type: "application/pdf" });
      } catch (fe: unknown) {
        logScanDevicePdfFailure(fe, {
          variant,
          sourcePageCount: pages.length,
          phase: "file_construct",
        });
        setError(null);
        syncScanConnectionModal(false);
        syncScanDevicePdfModal(true);
        return;
      }
      setBusyAwaitingHandoff(true);
      // Yield so the button can repaint with the handoff label before a long ``await onComplete``.
      await Promise.resolve();
      let closedBeforeHandoff = false;
      if (releaseUiBeforeOnComplete) {
        onClose();
        closedBeforeHandoff = true;
      }
      try {
        await Promise.resolve(
          onComplete(pdfFile, {
            ocrApplied: ocrStatus === "applied",
            ocrReason,
            sourcePageCount: pages.length,
          }),
        );
      } catch (handoffErr: unknown) {
        logScanDevicePdfFailure(handoffErr, {
          variant,
          sourcePageCount: pages.length,
          phase: "onComplete",
          releaseUiBeforeOnComplete: closedBeforeHandoff,
        });
        if (!closedBeforeHandoff) {
          setError(null);
          syncScanConnectionModal(false);
          syncScanDevicePdfModal(true);
        }
        return;
      }
      syncScanDevicePdfModal(false);
      if (!closedBeforeHandoff) {
        onClose();
      }
    } catch (e: unknown) {
      const res = (e as { response?: { status?: number; data?: unknown } }).response;
      const status = res?.status;
      const blob = res?.data;
      const mergeTransport = !(blob instanceof Blob) && isScanMergeTransportFailure(e);

      if (blob instanceof Blob) {
        syncScanConnectionModal(false);
        syncScanDevicePdfModal(false);
        const parsed = await parseBlobError(blob);
        if (parsed) {
          setError(parsed);
        } else {
          setError(t("documentScanner.errorGeneric"));
        }
      } else if (mergeTransport) {
        logScanMergeTransportFailure(e, {
          variant,
          sourcePageCount: pages.length,
        });
        setError(null);
        syncScanDevicePdfModal(false);
        syncScanConnectionModal(true);
      } else {
        syncScanConnectionModal(false);
        const msg =
          typeof e === "object" &&
          e &&
          "message" in e &&
          typeof (e as { message?: string }).message === "string"
            ? (e as { message: string }).message
            : "";
        const showDevicePdfModal = msg === "empty_pdf" || isLikelyDevicePdfFailure(e);
        if (showDevicePdfModal) {
          logScanDevicePdfFailure(e, {
            variant,
            sourcePageCount: pages.length,
            reason: msg === "empty_pdf" ? "empty_pdf" : "device_heuristic_after_merge",
          });
          setError(null);
          syncScanDevicePdfModal(true);
        } else if (status === 413) {
          syncScanDevicePdfModal(false);
          setError(t("documentScanner.errorGeneric"));
        } else {
          syncScanDevicePdfModal(false);
          setError(t("documentScanner.errorGeneric"));
        }
      }
    } finally {
      setBusyAwaitingHandoff(false);
      setBusy(false);
    }
  };

  /** Optional passport incomplete-page warning; otherwise merge immediately (same server path as passport). */
  const requestPassportAwareBuildPdf = () => {
    if (
      passportLowPageCountWarn &&
      pages.length > 0 &&
      pages.length < PASSPORT_EXPECTED_PAGES_WARN_THRESHOLD
    ) {
      setPassportLowPageWarnOpen(true);
      return;
    }
    void buildPdf();
  };

  const attemptCloseScannerSheet = useCallback(() => {
    if (busy || scanConnectionModalOpen || scanDevicePdfModalOpen) return;
    onClose();
  }, [busy, scanConnectionModalOpen, scanDevicePdfModalOpen, onClose]);

  /** Avoid closing sheet from the same tap/gesture that opened it (mobile ghost clickthrough). */
  const [backdropDismissEnabled, setBackdropDismissEnabled] = useState(false);
  useEffect(() => {
    if (!open) {
      setBackdropDismissEnabled(false);
      return;
    }
    setBackdropDismissEnabled(false);
    const id = window.setTimeout(() => setBackdropDismissEnabled(true), 450);
    return () => window.clearTimeout(id);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !mobile || previewPageId === null || pages.length === 0) return;
    const strip = thumbStripRef.current;
    if (!strip) return;
    const slots = strip.querySelectorAll("[data-gm-thumb-slot]");
    if (slots.length >= 2) {
      const r0 = slots[0].getBoundingClientRect();
      const r1 = slots[1].getBoundingClientRect();
      const slot = r1.left - r0.left;
      setThumbSlotPx((prev) => (Math.abs(prev - slot) < 0.75 ? prev : slot));
      return;
    }
    if (slots.length === 1) {
      const gapStr = getComputedStyle(strip).gap || getComputedStyle(strip).columnGap || "16px";
      const gap = parseFloat(gapStr) || 16;
      const slot = slots[0].getBoundingClientRect().width + gap;
      setThumbSlotPx((prev) => (Math.abs(prev - slot) < 0.75 ? prev : slot));
      return;
    }
    const first = strip.querySelector("[data-gm-thumb-idx]");
    if (!(first instanceof HTMLElement)) return;
    const w = first.getBoundingClientRect().width;
    const gapStr = getComputedStyle(strip).gap || getComputedStyle(strip).columnGap || "16px";
    const gap = parseFloat(gapStr) || 16;
    const slot = w + gap;
    setThumbSlotPx((prev) => (Math.abs(prev - slot) < 0.75 ? prev : slot));
  }, [open, mobile, previewPageId, pages.length]);

  if (!open) return null;

  const scannerErrorOverlayOpen = scanConnectionModalOpen || scanDevicePdfModalOpen;

  const previewEntry =
    previewPageId !== null ? pages.find((p) => p.id === previewPageId) : undefined;
  const previewIdx = previewEntry ? pages.findIndex((p) => p.id === previewEntry.id) : -1;

  const passportCornerEntry =
    variant === "passport" && passportPostCaptureEditId
      ? pages.find((p) => p.id === passportPostCaptureEditId)
      : undefined;

  const cornerEditTarget =
    passportCornerEntry ?? (cornerEditorOpen && previewEntry ? previewEntry : undefined);

  const pdfCreateBusyLabel =
    busy && !busyAwaitingHandoff
      ? t("documentScanner.scanMergeSaving")
      : busy && busyAwaitingHandoff && afterPdfBusyLabelKey
        ? t(afterPdfBusyLabelKey as Parameters<typeof t>[0])
        : t("documentScanner.processing");

  const cameraOverlay = (
    <DocumentCameraView
      open={cameraOpen && mobile}
      passportGuidePhase={
        variant === "passport" ? (pages.length === 0 ? "first" : "subsequent") : null
      }
      passportCapturePageNumber={variant === "passport" ? pages.length + 1 : undefined}
      onPassportOpenExamples={
        variant === "passport" && passportExamplesSuite ? () => setPassportExamplesOpen(true) : undefined
      }
      genericDocumentCamera={variant === "passport" && !passportExamplesSuite}
      onClose={closeCameraFromUi}
      onCapture={(file, meta) => {
        const insertAfter = pendingInsertAfterIdxRef.current;
        pendingInsertAfterIdxRef.current = null;
        const replaceIdx = passportReplacePageIdxRef.current;
        passportReplacePageIdxRef.current = null;

        if (variant === "passport") {
          const snapshot = pagesRef.current;
          if (replaceIdx !== null && replaceIdx >= 0 && replaceIdx < snapshot.length) {
            const pid = snapshot[replaceIdx].id;
            const old = snapshot[replaceIdx];
            try {
              URL.revokeObjectURL(old.previewUrl);
            } catch {
              /* ignore */
            }
            const previewUrl = URL.createObjectURL(file);
            setPages((prev) => {
              const next = [...prev];
              next[replaceIdx] = {
                id: pid,
                file,
                previewUrl,
                flattened: meta.flattened,
              };
              return next;
            });
            setPassportPostCaptureEditId(pid);
            setCameraOpen(false);
            setError(null);
            return;
          }

          const id = genId();
          const previewUrl = URL.createObjectURL(file);
          const newPage: ScanPage = { id, file, previewUrl, flattened: meta.flattened };
          setPages((prev) => {
            if (insertAfter !== null && insertAfter >= 0 && insertAfter < prev.length) {
              const next = [...prev];
              next.splice(insertAfter + 1, 0, newPage);
              return next;
            }
            return [...prev, newPage];
          });
          setPassportPostCaptureEditId(id);
          setCameraOpen(false);
          setError(null);
          return;
        }

        const id = genId();
        const previewUrl = URL.createObjectURL(file);
        const newPage: ScanPage = { id, file, previewUrl, flattened: meta.flattened };

        if (insertAfter !== null && insertAfter >= 0) {
          setPages((prev) => {
            const next = [...prev];
            if (insertAfter < next.length) {
              next.splice(insertAfter + 1, 0, newPage);
            } else {
              next.push(newPage);
            }
            return next;
          });
          setPreviewPageId(id);
        } else {
          appendCapturedPage(file, meta);
        }
        setCameraOpen(false);
        setError(null);
      }}
    />
  );

  const sheet = (
    <div
      className={`gm-scan-root fixed inset-0 z-[150] flex items-end justify-center${
        appearance === "dark" ? " gm-scan-root--dark" : ""
      }`}
      style={{ backgroundColor: appearance === "dark" ? "rgba(0,0,0,0.72)" : "rgba(0,0,0,0.45)" }}
      role="presentation"
      onClick={
        busy || scannerErrorOverlayOpen || !backdropDismissEnabled ? undefined : attemptCloseScannerSheet
      }
    >
      <style>{`
        @keyframes gmScanUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
      <div
        className={`gm-scan-sheet w-full max-w-[480px] rounded-t-[20px] bg-white shadow-xl flex flex-col max-h-[90vh]${
          appearance === "dark" ? " gm-scan-sheet--dark" : ""
        }`}
        style={{ animation: "gmScanUp 0.28s cubic-bezier(0.32,0.72,0,1)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gm-scan-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mt-3 shrink-0" />
        <div className="flex items-center justify-between gap-3 px-4 pt-2 pb-1 border-b border-slate-100 shrink-0">
          <h2 id="gm-scan-title" className="text-[17px] font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={attemptCloseScannerSheet}
            disabled={busy || scannerErrorOverlayOpen}
            className="min-w-[72px] h-10 text-[14px] font-medium text-slate-600 hover:text-slate-900 cursor-pointer disabled:opacity-40"
          >
            {t("documentScanner.close")}
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-4 py-4 flex flex-col gap-4">
          {!mobile ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center">
              <i className="ri-smartphone-line text-4xl text-slate-400 mb-3" aria-hidden="true" />
              <p className="text-[15px] font-medium text-slate-800 leading-relaxed">{t("documentScanner.desktopOnly")}</p>
            </div>
          ) : (
            <>
              {variant === "passport" ? (
                <>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={busy}
                    onChange={(e) => {
                      const ls = e.target.files;
                      if (ls?.length) appendFiles(ls);
                      e.target.value = "";
                    }}
                  />
                  {error ? (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
                  ) : null}
                  {!cameraOpen && pages.length === 0 ? (
                    passportExamplesSuite ? (
                      <div className="rounded-xl border border-emerald-100 bg-emerald-50/90 px-4 pt-5 pb-6 flex flex-col">
                        <div className="flex flex-col gap-3">
                          <p className="text-[16px] font-bold text-emerald-950 leading-snug">
                            {t("documentScanner.passportIntroTitle")}
                          </p>
                          <p className="text-[13px] text-emerald-900/95 leading-relaxed">
                            {t("documentScanner.passportIntroHelp")}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setCameraOpen(true)}
                          className="mt-7 w-full min-h-[52px] flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 disabled:opacity-50 shrink-0"
                        >
                          <i className="ri-camera-line text-xl" aria-hidden="true" />
                          {t("documentScanner.passportTakePhoto")}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setPassportExamplesOpen(true)}
                          className="mt-3 w-full min-h-[44px] flex items-center justify-center gap-2 rounded-xl border border-emerald-300/70 bg-white text-emerald-800 text-[14px] font-semibold hover:bg-emerald-50 disabled:opacity-50 shrink-0 touch-manipulation"
                        >
                          <i className="ri-image-2-line text-lg" aria-hidden="true" />
                          {t("documentScanner.passportViewExamplesLink")}
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 pt-5 pb-6 flex flex-col gap-3">
                        <p className="text-[16px] font-bold text-slate-900 leading-snug">{t(titleKey)}</p>
                        <p className="text-[13px] text-slate-600 leading-relaxed">{t("documentScanner.multiPageHint")}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setCameraOpen(true)}
                            className="flex-1 min-w-[140px] min-h-[48px] flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[14px] font-semibold hover:bg-emerald-700 disabled:opacity-50"
                          >
                            <i className="ri-camera-line text-lg" aria-hidden="true" />
                            {t("documentScanner.addPages")}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => galleryInputRef.current?.click()}
                            className="min-w-[120px] min-h-[48px] flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-[14px] font-medium hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                          >
                            <i className="ri-image-line text-lg" aria-hidden="true" />
                            {t("documentScanner.fromGallery")}
                          </button>
                        </div>
                      </div>
                    )
                  ) : null}
                  {!cameraOpen && pages.length > 0 && passportReviewSkipped ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 flex flex-col gap-3">
                      <p className="text-[13px] text-slate-600 leading-relaxed">{t("documentScanner.passportSheetMinimalHint")}</p>
                      <button
                        type="button"
                        disabled={busy || scannerErrorOverlayOpen}
                        onClick={() => requestPassportAwareBuildPdf()}
                        className="w-full min-h-[48px] rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation"
                      >
                        <i className="ri-file-pdf-line text-lg" aria-hidden="true" />
                        {t("documentScanner.buildPdf")}
                      </button>
                      <button
                        type="button"
                        disabled={busy || scannerErrorOverlayOpen}
                        onClick={() => onClose()}
                        className="w-full min-h-[44px] rounded-xl border border-slate-300 bg-white text-slate-800 text-[14px] font-semibold hover:bg-slate-50 disabled:opacity-50 touch-manipulation"
                      >
                        {t("documentScanner.passportCancel")}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setCameraOpen(true)}
                      className="flex-1 min-w-[140px] min-h-[44px] flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[14px] font-semibold hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <i className="ri-scan-line text-lg" />
                      {pages.length > 0 ? t("documentScanner.addAnotherPage") : t("documentScanner.addPages")}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => galleryInputRef.current?.click()}
                      className="min-w-[120px] min-h-[44px] flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-[14px] font-medium hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <i className="ri-image-line text-lg" />
                      {t("documentScanner.fromGallery")}
                    </button>
                  </div>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={busy}
                    onChange={(e) => {
                      const ls = e.target.files;
                      if (ls?.length) appendFiles(ls);
                      e.target.value = "";
                    }}
                  />

                  {error ? (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
                  ) : null}

                  {pages.length > 0 ? (
                    <ul className="flex flex-col gap-2">
                      {pages.map((p, idx) => (
                        <li
                          key={p.id}
                          className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2"
                        >
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setPreviewPageId(p.id)}
                            className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:pointer-events-none active:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                            aria-label={`${t("documentScanner.previewThumbnailAria")}, ${t("documentScanner.pageLabel").replace("{{n}}", String(idx + 1))}`}
                          >
                            <img src={p.previewUrl} alt="" className="w-full h-full object-cover min-h-[56px]" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-slate-800 flex items-center gap-1.5">
                              {t("documentScanner.pageLabel").replace("{{n}}", String(idx + 1))}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">{formatFileShortName(p.file)}</p>
                          </div>
                          <div className="flex flex-col gap-0.5 shrink-0">
                            <button
                              type="button"
                              disabled={busy || idx === 0}
                              onClick={() => move(idx, -1)}
                              className="px-2 py-1 text-[11px] text-slate-600 border border-slate-200 rounded-md disabled:opacity-30 cursor-pointer hover:bg-slate-50"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              disabled={busy || idx === pages.length - 1}
                              onClick={() => move(idx, 1)}
                              className="px-2 py-1 text-[11px] text-slate-600 border border-slate-200 rounded-md disabled:opacity-30 cursor-pointer hover:bg-slate-50"
                            >
                              ↓
                            </button>
                          </div>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => removePage(p.id)}
                            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 cursor-pointer"
                            aria-label={t("documentScanner.remove")}
                          >
                            <i className="ri-delete-bin-line text-lg" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[13px] text-slate-500 text-center py-6">{t("documentScanner.needOnePage")}</p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {mobile && variant !== "passport" ? (
          <div className="shrink-0 border-t border-slate-100 px-4 pt-4 pb-[max(24px,calc(env(safe-area-inset-bottom,0px)+52px))] bg-white">
            <button
              type="button"
              disabled={busy || scannerErrorOverlayOpen || pages.length === 0}
              onClick={() => requestPassportAwareBuildPdf()}
              className="w-full min-h-[50px] flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[15px] disabled:opacity-45 disabled:pointer-events-none"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl" aria-hidden="true" />
                  {pdfCreateBusyLabel}
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line text-xl" aria-hidden="true" />
                  {t("documentScanner.buildPdf")}
                </>
              )}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  const modalTree = (
    <>
      {sheet}
      {previewEntry && mobile ? (
        <div
          className={`gm-scan-review fixed inset-0 z-[165] flex flex-col bg-[#f5f4f1]${
            appearance === "dark" ? " gm-scan-review--dark" : ""
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-scan-preview-title"
          aria-describedby="gm-scan-preview-hint"
        >
          <header className="shrink-0 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 border-b border-slate-200 bg-[#f5f4f1]">
            <button
              type="button"
              disabled={busy || scannerErrorOverlayOpen}
              onClick={() => {
                if (pagesRef.current.length > 0) {
                  setReviewLeaveConfirmOpen(true);
                  return;
                }
                if (variant === "passport") setPassportReviewSkipped(true);
                setPreviewPageId(null);
              }}
              className="justify-self-start min-h-[44px] px-1 text-[15px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40 touch-manipulation"
            >
              {t("documentScanner.close")}
            </button>
            <h2 id="gm-scan-preview-title" className="text-[17px] font-bold text-slate-900 text-center whitespace-nowrap">
              {t("documentScanner.reviewScreenTitle")}
            </h2>
            {variant === "passport" && passportExamplesSuite ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setPassportExamplesOpen(true)}
                aria-label={t("documentScanner.passportExamplesAria")}
                className="justify-self-end min-w-[44px] min-h-[44px] px-2 flex items-center justify-center rounded-xl text-slate-600 hover:bg-slate-200/70 hover:text-emerald-700 disabled:opacity-40 touch-manipulation font-bold text-[20px] leading-none"
              >
                <span aria-hidden="true">?</span>
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => setReviewDeleteConfirmOpen(true)}
                aria-label={t("documentScanner.reviewDeletePageAria")}
                className="justify-self-end w-11 h-11 flex items-center justify-center rounded-xl text-slate-600 hover:bg-slate-200/70 hover:text-red-600 disabled:opacity-40 touch-manipulation"
              >
                <i className="ri-delete-bin-line text-[22px]" aria-hidden="true" />
              </button>
            )}
          </header>

          <div className="shrink-0 px-4 pt-3 pb-1">
            <p className="text-[13px] font-medium text-slate-500">
              {t("documentScanner.reviewPageOf")
                .replace("{{current}}", String(previewIdx + 1))
                .replace("{{total}}", String(pages.length))}
            </p>
          </div>

          <div className="flex-1 min-h-0 flex flex-col px-4 overflow-hidden pb-2">
            <div className="relative flex-1 min-h-[180px] rounded-2xl bg-[#1a2332] overflow-hidden flex items-center justify-center p-3">
              <img
                src={previewEntry.previewUrl}
                alt=""
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                className="max-w-full max-h-[min(48vh,calc(100dvh-380px))] w-auto h-auto object-contain select-none pointer-events-none [-webkit-touch-callout:none]"
              />
              <div className="absolute top-3 right-3 z-[15] flex flex-wrap justify-end gap-2 max-w-[min(100%,calc(100vw-24px))]">
                {variant === "passport" ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => syncPassportReviewFullView(true)}
                    aria-label={t("documentScanner.passportReviewViewAria")}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3.5 py-2 text-[14px] font-semibold text-slate-800 shadow-md hover:bg-white disabled:opacity-45 touch-manipulation"
                  >
                    <i className="ri-eye-line text-lg" aria-hidden="true" />
                    {t("documentScanner.passportReviewView")}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    syncPassportReviewFullView(false);
                    setCornerEditorOpen(true);
                  }}
                  aria-label={t("documentScanner.cornerAdjustAria")}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[14px] font-semibold text-slate-800 shadow-md hover:bg-slate-50 disabled:opacity-45 touch-manipulation"
                >
                  <i className="ri-pencil-line text-lg" aria-hidden="true" />
                  {t("documentScanner.reviewEdit")}
                </button>
              </div>
            </div>

            <p id="gm-scan-preview-hint" className="sr-only">
              {variant === "passport" && passportExamplesSuite
                ? t("documentScanner.passportPreviewReviewHint")
                : t("documentScanner.previewHint")}
            </p>

            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em] mt-5 mb-2 shrink-0">
              {t("documentScanner.reviewPagesSectionLabel")}
            </p>
            <p className="text-[12px] text-slate-500 mb-3 shrink-0 leading-snug">{t("documentScanner.reviewReorderHint")}</p>
            <div
              ref={thumbStripRef}
              className={`flex gap-4 overflow-x-auto overscroll-x-contain pb-1 shrink-0 [-webkit-overflow-scrolling:touch] relative ${
                thumbStripReorderLock ? "touch-none" : ""
              }`}
            >
              {pages.map((p, idx) => {
                const sel = p.id === previewPageId;
                const dragFrom = thumbnailDragFromIdx;
                const hoverSlot = dragFrom !== null ? thumbnailHoverDropIdx ?? dragFrom : null;
                const previewTx =
                  dragFrom !== null && hoverSlot !== null
                    ? thumbnailReorderPreviewOffsetPx(idx, dragFrom, hoverSlot, thumbSlotPx)
                    : 0;
                const reorderAnimating = dragFrom !== null;
                const isDraggedThumb = dragFrom === idx;
                const transform =
                  isDraggedThumb && previewTx !== 0
                    ? `translateX(${previewTx}px) scale(1.035)`
                    : isDraggedThumb
                      ? "scale(1.035)"
                      : previewTx !== 0
                        ? `translateX(${previewTx}px)`
                        : undefined;
                const thumbStyle: CSSProperties | undefined =
                  transform !== undefined
                    ? { transform, willChange: reorderAnimating ? "transform" : undefined }
                    : undefined;
                const pagePhrase = t("documentScanner.pageLabel").replace("{{n}}", String(idx + 1));
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-gm-thumb-slot={idx}
                    data-gm-thumb-idx={idx}
                    disabled={busy}
                    onPointerDown={busy ? undefined : beginThumbnailReorderGesture(idx)}
                    onClick={() => {
                      if (thumbSkipNextClickRef.current) {
                        thumbSkipNextClickRef.current = false;
                        return;
                      }
                      setPreviewPageId(p.id);
                    }}
                    style={thumbStyle}
                    className={`relative z-0 shrink-0 h-[118px] w-[86px] overflow-hidden rounded-2xl border bg-white shadow-sm outline-none select-none touch-manipulation [-webkit-tap-highlight-color:transparent] focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f5f4f1] ${
                      reorderAnimating ? "transition-none" : "transition-[transform,box-shadow,border-color] duration-200 ease-out"
                    } ${
                      isDraggedThumb
                        ? "z-30 border-emerald-500 shadow-xl ring-2 ring-emerald-500/30"
                        : !isDraggedThumb && sel
                          ? "border-emerald-500 ring-2 ring-emerald-500/25 shadow-md"
                          : "border-slate-200/90 hover:border-slate-300 hover:shadow"
                    } ${busy ? "opacity-45 pointer-events-none" : ""}`}
                    aria-current={sel ? "true" : undefined}
                    aria-label={`${t("documentScanner.previewThumbnailAria")}, ${pagePhrase}. ${t("documentScanner.reviewReorderHoldA11y")}`}
                  >
                    <img
                      src={p.previewUrl}
                      alt=""
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none select-none [-webkit-touch-callout:none]"
                    />
                    <div
                      className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/45 via-black/15 to-transparent"
                      aria-hidden="true"
                    />
                    <span className="pointer-events-none absolute top-2 left-2 z-[1] flex min-h-[24px] min-w-[24px] items-center justify-center rounded-lg bg-black/55 px-2 text-[12px] font-bold tabular-nums text-white shadow-sm backdrop-blur-[2px]">
                      {idx + 1}
                    </span>
                    {/*
                      Dedicated drag handle: `touch-action: none` keeps the browser from preempting
                      the gesture with horizontal scroll, so reorder activates the moment the user
                      touches it (no long-press race). The thumbnail body still responds to taps and
                      long-press via the button's own onPointerDown.
                    */}
                    <span
                      role="button"
                      aria-label={t("documentScanner.reviewReorderHoldA11y")}
                      onPointerDown={
                        busy
                          ? undefined
                          : beginThumbnailReorderGesture(idx, { immediate: true })
                      }
                      onClick={(ev) => ev.stopPropagation()}
                      style={{ touchAction: "none" }}
                      className="absolute top-1.5 right-1.5 z-[3] flex h-8 w-8 items-center justify-center rounded-lg bg-black/65 text-white shadow-sm backdrop-blur-[2px] cursor-grab active:cursor-grabbing select-none touch-manipulation"
                    >
                      <i className="ri-draggable text-[17px] leading-none" aria-hidden="true" />
                    </span>
                    <span
                      className="pointer-events-none absolute bottom-2 left-1/2 z-[1] flex -translate-x-1/2 gap-[3px] opacity-[0.82]"
                      aria-hidden="true"
                    >
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="h-[3px] w-[3px] rounded-full bg-white shadow-sm" />
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const idx = previewIdx >= 0 ? previewIdx : pages.length - 1;
                pendingInsertAfterIdxRef.current = idx >= 0 ? idx : null;
                setPreviewPageId(null);
                setCameraOpen(true);
              }}
              className="mt-3 shrink-0 w-full min-h-[48px] rounded-xl border border-slate-300 bg-white text-slate-800 text-[14px] font-semibold hover:bg-slate-50 flex items-center justify-center gap-2 disabled:opacity-45 touch-manipulation"
            >
              <i className="ri-add-line text-xl" aria-hidden="true" />
              {t("documentScanner.reviewInsertPage")}
            </button>
          </div>

          <footer className="shrink-0 px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] border-t border-slate-200 bg-[#f5f4f1]">
            <button
              type="button"
              disabled={busy || scannerErrorOverlayOpen}
              onClick={() => requestPassportAwareBuildPdf()}
              className="w-full min-h-[50px] rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 disabled:opacity-45 flex items-center justify-center gap-2 touch-manipulation"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl" aria-hidden="true" />
                  {pdfCreateBusyLabel}
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line text-xl" aria-hidden="true" />
                  {t("documentScanner.buildPdf")}
                </>
              )}
            </button>
          </footer>
        </div>
      ) : null}
      {previewEntry && mobile && variant === "passport" && passportReviewFullViewOpen ? (
        <div
          className="fixed inset-0 z-[170] flex flex-col bg-black"
          role="dialog"
          aria-modal="true"
          aria-label={t("documentScanner.passportReviewViewAria")}
        >
          <div className="shrink-0 flex items-center justify-between gap-3 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 bg-black/90 border-b border-white/10">
            <button
              type="button"
              disabled={busy}
              onClick={() => syncPassportReviewFullView(false)}
              className="min-h-[44px] px-3 text-[15px] font-semibold text-white/95 hover:text-white disabled:opacity-40 touch-manipulation"
            >
              {t("documentScanner.close")}
            </button>
            <p className="text-[13px] font-medium text-white/80 tabular-nums text-center truncate min-w-0 flex-1">
              {t("documentScanner.passportReviewViewPage")
                .replace("{{current}}", String(previewIdx + 1))
                .replace("{{total}}", String(pages.length))}
            </p>
            <span className="min-w-[56px]" aria-hidden="true" />
          </div>
          <button
            type="button"
            disabled={busy}
            className="flex-1 min-h-0 flex items-center justify-center p-4 w-full tap-highlight-transparent"
            onClick={() => syncPassportReviewFullView(false)}
            aria-label={t("documentScanner.passportReviewViewDismissBackdrop")}
          >
            <span className="sr-only">{t("documentScanner.passportReviewViewDismissBackdrop")}</span>
            <img
              src={previewEntry.previewUrl}
              alt=""
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[min(88dvh,calc(100dvh-140px))] w-auto h-auto object-contain select-none pointer-events-auto [-webkit-touch-callout:none]"
            />
          </button>
          <p className="sr-only">{t("documentScanner.passportReviewViewAria")}</p>
        </div>
      ) : null}
      {mobile && open && passportLowPageWarnOpen && passportLowPageCountWarn ? (
        <div
          className="fixed inset-0 z-[173] flex items-end justify-center bg-black/55 px-3 pt-10"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-scan-passport-pages-title"
          onClick={() => setPassportLowPageWarnOpen(false)}
        >
          <div
            className="w-full max-w-[440px] rounded-t-[20px] bg-white px-5 pt-6 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="gm-scan-passport-pages-title" className="text-[17px] font-bold text-slate-900 text-center mb-2">
              {t("documentScanner.passportLowPageWarnTitle")}
            </h3>
            <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6 px-1">
              {t("documentScanner.passportLowPageWarnBody")}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => setPassportLowPageWarnOpen(false)}
              className="w-full min-h-[50px] rounded-xl border border-slate-300 bg-white text-slate-900 text-[15px] font-semibold hover:bg-slate-50 mb-3 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.passportLowPageWarnReview")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPassportLowPageWarnOpen(false);
                void buildPdf();
              }}
              className="w-full min-h-[50px] rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.passportLowPageWarnAnyway")}
            </button>
          </div>
        </div>
      ) : null}
      {previewEntry && mobile && reviewDeleteConfirmOpen ? (
        <div
          className="fixed inset-0 z-[172] flex items-end justify-center bg-black/55 px-3 pt-10"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-scan-delete-title"
          onClick={() => setReviewDeleteConfirmOpen(false)}
        >
          <div
            className="w-full max-w-[440px] rounded-t-[20px] bg-white px-5 pt-6 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="gm-scan-delete-title" className="text-[17px] font-bold text-slate-900 text-center mb-2">
              {t("documentScanner.reviewDeleteConfirmTitle")}
            </h3>
            <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6 px-1">
              {t("documentScanner.reviewDeleteConfirmBody")}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                removePage(previewEntry.id);
                setReviewDeleteConfirmOpen(false);
              }}
              className="w-full min-h-[50px] rounded-xl bg-red-600 hover:bg-red-700 text-white text-[15px] font-semibold mb-3 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.reviewDeleteConfirmDelete")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setReviewDeleteConfirmOpen(false)}
              className="w-full min-h-[50px] rounded-xl border border-slate-300 bg-white text-slate-900 text-[15px] font-semibold hover:bg-slate-50 touch-manipulation"
            >
              {t("documentScanner.reviewDeleteConfirmCancel")}
            </button>
          </div>
        </div>
      ) : null}
      {previewEntry && mobile && reviewLeaveConfirmOpen ? (
        <div
          className="fixed inset-0 z-[174] flex items-end justify-center bg-black/55 px-3 pt-10"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-scan-leave-review-title"
          onClick={() => setReviewLeaveConfirmOpen(false)}
        >
          <div
            className="w-full max-w-[440px] rounded-t-[20px] bg-white px-5 pt-6 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="gm-scan-leave-review-title" className="text-[17px] font-bold text-slate-900 text-center mb-2">
              {t("documentScanner.reviewLeaveConfirmTitle")}
            </h3>
            <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6 px-1">
              {t("documentScanner.reviewLeaveConfirmBody")}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => setReviewLeaveConfirmOpen(false)}
              className="w-full min-h-[50px] rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 mb-3 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.reviewLeaveConfirmStay")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setReviewLeaveConfirmOpen(false);
                onClose();
              }}
              className="w-full min-h-[50px] rounded-xl border border-red-200 bg-white text-red-700 text-[15px] font-semibold hover:bg-red-50 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.reviewLeaveConfirmDiscard")}
            </button>
          </div>
        </div>
      ) : null}
      {mobile && open && scanConnectionModalOpen ? (
        <div
          className="fixed inset-0 z-[182] flex items-end justify-center bg-black/55 px-3 pt-10"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-scan-connection-title"
        >
          <div
            className="w-full max-w-[440px] rounded-t-[20px] bg-white px-5 pt-6 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="gm-scan-connection-title" className="text-[17px] font-bold text-slate-900 text-center mb-2">
              {t("documentScanner.connectionProblemTitle")}
            </h3>
            <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6 px-1">
              {t("documentScanner.connectionProblemBody")}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void buildPdf()}
              className="w-full min-h-[50px] rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 mb-3 disabled:opacity-45 touch-manipulation flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl" aria-hidden="true" />
                  {pdfCreateBusyLabel}
                </>
              ) : (
                t("documentScanner.connectionProblemRetry")
              )}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                syncScanConnectionModal(false);
                syncScanDevicePdfModal(false);
                onClose();
              }}
              className="w-full min-h-[50px] rounded-xl border border-slate-300 bg-white text-slate-900 text-[15px] font-semibold hover:bg-slate-50 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.connectionProblemCancel")}
            </button>
          </div>
        </div>
      ) : null}
      {mobile && open && scanDevicePdfModalOpen ? (
        <div
          className="fixed inset-0 z-[183] flex items-end justify-center bg-black/55 px-3 pt-10"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-scan-device-pdf-title"
        >
          <div
            className="w-full max-w-[440px] rounded-t-[20px] bg-white px-5 pt-6 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-center mb-1">
              {t("documentScanner.devicePdfFailureKicker")}
            </p>
            <h3 id="gm-scan-device-pdf-title" className="text-[17px] font-bold text-slate-900 text-center mb-2">
              {t("documentScanner.devicePdfFailureTitle")}
            </h3>
            <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6 px-1">
              {t("documentScanner.devicePdfFailureBody")}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void buildPdf()}
              className="w-full min-h-[50px] rounded-xl border border-emerald-600 bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 mb-3 disabled:opacity-45 touch-manipulation flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl" aria-hidden="true" />
                  {pdfCreateBusyLabel}
                </>
              ) : (
                t("documentScanner.devicePdfFailureRetry")
              )}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => syncScanDevicePdfModal(false)}
              className="w-full min-h-[50px] rounded-xl border border-slate-300 bg-white text-slate-900 text-[15px] font-semibold hover:bg-slate-50 disabled:opacity-45 touch-manipulation"
            >
              {t("documentScanner.devicePdfFailureCancel")}
            </button>
          </div>
        </div>
      ) : null}
      {cornerEditTarget && mobile ? (
        <DocumentCornerEditor
          open
          imageUrl={cornerEditTarget.previewUrl}
          file={cornerEditTarget.file}
          pageNumber={Math.max(1, pages.findIndex((p) => p.id === cornerEditTarget.id) + 1)}
          variant={passportCornerEntry ? "passport" : variant}
          passportFooter={
            passportCornerEntry ? "post_capture" : variant === "passport" && cornerEditorOpen ? "review_edit" : null
          }
          onPassportRetakeFromReview={
            variant === "passport" && cornerEditorOpen && previewEntry && !passportCornerEntry
              ? () => {
                  const liveIdx = pagesRef.current.findIndex((p) => p.id === previewEntry.id);
                  passportReplacePageIdxRef.current = liveIdx >= 0 ? liveIdx : null;
                  setCornerEditorOpen(false);
                  setPreviewPageId(null);
                  setCameraOpen(true);
                }
              : undefined
          }
          onClose={() => {
            if (passportCornerEntry) {
              removePage(passportCornerEntry.id);
              setPassportPostCaptureEditId(null);
              setCameraOpen(true);
              setError(null);
              return;
            }
            setCornerEditorOpen(false);
          }}
          onApply={(newFile, opts) => {
            const pid = cornerEditTarget.id;
            const url = URL.createObjectURL(newFile);
            setPages((prev) =>
              prev.map((p) => {
                if (p.id !== pid) return p;
                try {
                  URL.revokeObjectURL(p.previewUrl);
                } catch {
                  /* ignore */
                }
                return { ...p, file: newFile, previewUrl: url, flattened: true };
              }),
            );
            if (passportCornerEntry) {
              setPassportPostCaptureEditId(null);
              if (opts?.intent === "add_page") {
                setCameraOpen(true);
              } else {
                setPreviewPageId(pid);
                setPassportReviewSkipped(false);
              }
              setError(null);
            } else {
              setCornerEditorOpen(false);
            }
          }}
        />
      ) : null}
      {passportExamplesOpen && variant === "passport" && passportExamplesSuite ? (
        <PassportScanExamplesModal onClose={dismissPassportExamples} />
      ) : null}
      {cameraOverlay}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modalTree, document.body);
}
